/**
 * Auswahl - Adress-Selektion aus dem WPool
 *
 * Ermöglicht die Auswahl von Adressen nach verschiedenen Kriterien:
 * - HKN (Herkunft)
 * - CHK Status
 * - Zufällige oder geordnete Auswahl
 * - Export der selektierten Adressen
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListChecks, FunnelSimple, Play, Download, ArrowCounterClockwise, Trash, FloppyDisk, Plus, CircleNotch } from '@phosphor-icons/react';
import SubPageHeader from '../components/SubPageHeader';
import { getIngestUrl, authFetch } from '../config';
import { formatDateTime } from '../utils/dateUtils';

interface HknInfo {
  hkn: string;
  total: number;
  available: number;
  selected: number;
  sent: number;
}

interface LfrInfo {
  lfr: string;
  count: number;
}

interface AuswahlPreview {
  matchedCount: number;
  byHkn: { hkn: string; count: number }[];
  byLfr: { lfr: string; count: number }[];
}

interface AuswahlRun {
  id: number;
  name: string;
  filterConfig: any;
  selectionMode: string;
  requestedCount: number | null;
  matchedCount: number;
  selectedCount: number;
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  exportedAt: string | null;
  exportFile: string | null;
  createdAt: string;
}

interface Profile {
  id: number;
  name: string;
  description: string;
  filterConfig: any;
}

interface PlanungSummary {
  id: number;
  name: string;
  status: string;
}

interface PlanungKw {
  id: number;
  kw_nummer: number;
  jahr: number;
  kw_label: string;
  visible: boolean;
}

interface PlanungQuelle {
  id: number;
  name: string;
  wkz: string | null;
  kategorie: 'eigen' | 'fremd';
  wpool_hkn: string | null;
  bestand: number;
}

interface PlanungKwDatenRow {
  quelle_id: number;
  kw_config_id: number;
  einsatz: number | null;
}

interface PlanungEinsatzZeile {
  quelle: PlanungQuelle;
  einsatz: number;
}

export default function Auswahl() {
  const navigate = useNavigate();

  // State
  const [activeTab, setActiveTab] = useState<'new' | 'runs' | 'profiles'>('new');
  const [hknList, setHknList] = useState<HknInfo[]>([]);
  const [lfrList, setLfrList] = useState<LfrInfo[]>([]);
  const [runs, setRuns] = useState<AuswahlRun[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filter State
  const [selectedHkn, setSelectedHkn] = useState<string[]>([]);
  const [excludeHkn, setExcludeHkn] = useState<string[]>([]);
  const [plzPrefix, setPlzPrefix] = useState<string>('');
  const [selectionMode, setSelectionMode] = useState<'random' | 'newest' | 'oldest' | 'all'>('random');
  const [requestedCount, setRequestedCount] = useState<string>('1000');
  const [auswahlName, setAuswahlName] = useState<string>('');

  // Preview
  const [preview, setPreview] = useState<AuswahlPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Selected run for detail view
  const [selectedRun, setSelectedRun] = useState<AuswahlRun | null>(null);
  const [selectedRecords, setSelectedRecords] = useState<any[]>([]);

  // Planungs-Modus: Werte aus einer Einsatzplanung übernehmen
  const [planungenList, setPlanungenList] = useState<PlanungSummary[]>([]);
  const [planungId, setPlanungId] = useState<number | ''>('');
  const [planungKws, setPlanungKws] = useState<PlanungKw[]>([]);
  const [planungKwId, setPlanungKwId] = useState<number | ''>('');
  const [planungZeilen, setPlanungZeilen] = useState<PlanungEinsatzZeile[]>([]);
  const [planungLoading, setPlanungLoading] = useState(false);
  // Quellen, die explizit aus der Vorschau/Auswahl ausgeschlossen werden (per Checkbox)
  const [excludedQuelleIds, setExcludedQuelleIds] = useState<Set<number>>(new Set());

  // Verfügbare Export-Felder (vom Backend) und aktuell ausgewählte Felder
  const DEFAULT_EXPORT_FIELDS = ['akn', 'vnm', 'nnm', 'str', 'plz', 'ort', 'hkn', 'hkd', 'lfr', 'tns'];
  const [exportFields, setExportFields] = useState<{ key: string; label: string }[]>([]);
  const [selectedExportFields, setSelectedExportFields] = useState<Set<string>>(new Set(DEFAULT_EXPORT_FIELDS));

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [hknRes, lfrRes, runsRes, profilesRes] = await Promise.all([
        authFetch(getIngestUrl('/auswahl/hkn')),
        authFetch(getIngestUrl('/auswahl/lfr')),
        authFetch(getIngestUrl('/auswahl/runs')),
        authFetch(getIngestUrl('/auswahl/profiles'))
      ]);

      if (hknRes.ok) {
        const hknData = await hknRes.json();
        setHknList(hknData.data || []);
      }
      if (lfrRes.ok) {
        const lfrData = await lfrRes.json();
        setLfrList(lfrData.data || []);
      }
      if (runsRes.ok) {
        const runsData = await runsRes.json();
        setRuns(runsData.data || []);
      }
      if (profilesRes.ok) {
        const profilesData = await profilesRes.json();
        setProfiles(profilesData.data || []);
      }
    } catch (err) {
      setError('Fehler beim Laden der Daten');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Export-Felder laden (Whitelist vom Backend)
  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(getIngestUrl('/auswahl/export-fields'));
        if (res.ok) {
          const data = await res.json();
          setExportFields(data.data || []);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Export-Felder laden fehlgeschlagen', err);
      }
    })();
  }, []);

  // Planungen laden (für „Aus Planung übernehmen")
  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(getIngestUrl('/einsatzplanung/planungen'));
        if (res.ok) {
          const data = await res.json();
          setPlanungenList(data.data || []);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Planungen laden fehlgeschlagen', err);
      }
    })();
  }, []);

  // KWs der gewählten Planung laden
  useEffect(() => {
    if (!planungId) {
      setPlanungKws([]);
      setPlanungKwId('');
      setPlanungZeilen([]);
      return;
    }
    (async () => {
      try {
        const res = await authFetch(getIngestUrl(`/einsatzplanung/planungen/${planungId}/kw`));
        if (res.ok) {
          const data = await res.json();
          const kws: PlanungKw[] = (data.data || [])
            .filter((k: PlanungKw) => k.visible)
            .sort((a: PlanungKw, b: PlanungKw) =>
              a.jahr !== b.jahr ? a.jahr - b.jahr : a.kw_nummer - b.kw_nummer
            );
          setPlanungKws(kws);
          setPlanungKwId(kws[0]?.id ?? '');
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Planungs-KWs laden fehlgeschlagen', err);
      }
    })();
  }, [planungId]);

  // Bei Wechsel von Planung+KW: Einsatz-Werte pro Quelle laden
  // und Quellen-Auswahl (Checkboxen) zurücksetzen — alle aktiv per Default.
  useEffect(() => {
    if (!planungId || !planungKwId) {
      setPlanungZeilen([]);
      setExcludedQuelleIds(new Set());
      return;
    }
    (async () => {
      setPlanungLoading(true);
      try {
        const [quellenRes, datenRes] = await Promise.all([
          authFetch(getIngestUrl(`/einsatzplanung/planungen/${planungId}/quellen`)),
          authFetch(getIngestUrl(`/einsatzplanung/planungen/${planungId}/daten`)),
        ]);
        if (!quellenRes.ok || !datenRes.ok) {
          setPlanungZeilen([]);
          return;
        }
        const quellenData = await quellenRes.json();
        const datenData = await datenRes.json();
        const quellen: PlanungQuelle[] = quellenData.data || [];
        const daten: PlanungKwDatenRow[] = datenData.data || [];
        const einsatzByQuelle = new Map<number, number>();
        for (const d of daten) {
          if (d.kw_config_id !== planungKwId) continue;
          if (d.einsatz != null && d.einsatz > 0) {
            einsatzByQuelle.set(d.quelle_id, d.einsatz);
          }
        }
        const zeilen: PlanungEinsatzZeile[] = quellen
          .filter(q => einsatzByQuelle.has(q.id))
          .map(q => ({ quelle: q, einsatz: einsatzByQuelle.get(q.id) || 0 }))
          .sort((a, b) => b.einsatz - a.einsatz);
        setPlanungZeilen(zeilen);
        setExcludedQuelleIds(new Set()); // alle Quellen standardmäßig aktiv

        const planung = planungenList.find(p => p.id === planungId);
        const kw = planungKws.find(k => k.id === planungKwId);
        if (planung && kw) {
          setAuswahlName(`${planung.name} — ${kw.kw_label}/${kw.jahr}`);
        }
      } finally {
        setPlanungLoading(false);
      }
    })();
  }, [planungId, planungKwId, planungenList, planungKws]);

  // Aktive Zeilen (Checkbox an) → Filter (HKN-Liste, Anzahl) ableiten.
  // Reagiert auf Checkbox-Toggles, ohne den Server neu zu fragen.
  const aktiveZeilen = useMemo(
    () => planungZeilen.filter(z => !excludedQuelleIds.has(z.quelle.id)),
    [planungZeilen, excludedQuelleIds],
  );

  useEffect(() => {
    if (planungZeilen.length === 0) return;
    const hkns = Array.from(new Set(
      aktiveZeilen
        .map(z => z.quelle.wpool_hkn || z.quelle.wkz)
        .filter((h): h is string => !!h)
    ));
    const sum = aktiveZeilen.reduce((acc, z) => acc + z.einsatz, 0);
    setSelectedHkn(hkns);
    setExcludeHkn([]);
    setRequestedCount(String(sum));
  }, [aktiveZeilen, planungZeilen.length]);

  const toggleQuelle = (quelleId: number) => {
    setExcludedQuelleIds(prev => {
      const next = new Set(prev);
      if (next.has(quelleId)) next.delete(quelleId);
      else next.add(quelleId);
      return next;
    });
  };

  const toggleAllQuellen = (selectAll: boolean) => {
    if (selectAll) setExcludedQuelleIds(new Set());
    else setExcludedQuelleIds(new Set(planungZeilen.map(z => z.quelle.id)));
  };

  // Build filter from current state
  const buildFilter = () => {
    const filter: any = {};
    if (selectedHkn.length > 0) filter.hkn = selectedHkn;
    if (excludeHkn.length > 0) filter.hknExclude = excludeHkn;
    if (plzPrefix.trim()) filter.plzPrefix = plzPrefix.split(',').map(p => p.trim()).filter(p => p);
    return filter;
  };

  // Preview selection
  const handlePreview = async () => {
    setPreviewLoading(true);
    setError(null);
    try {
      const filter = buildFilter();
      const response = await authFetch(getIngestUrl('/auswahl/preview'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filter })
      });

      if (response.ok) {
        const data = await response.json();
        setPreview(data.data);
      } else {
        const errData = await response.json();
        setError(errData.message || 'Preview fehlgeschlagen');
      }
    } catch (err) {
      setError('Fehler beim Preview');
      console.error(err);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Execute selection
  const handleExecute = async () => {
    if (!auswahlName.trim()) {
      setError('Bitte Namen eingeben');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const config = {
        name: auswahlName.trim(),
        filter: buildFilter(),
        selectionMode,
        requestedCount: selectionMode !== 'all' ? parseInt(requestedCount, 10) : null
      };

      const response = await authFetch(getIngestUrl('/auswahl/execute'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(`${data.data.selectedCount} Adressen ausgewählt`);
        setActiveTab('runs');
        loadData();
        // Reset form
        setAuswahlName('');
        setSelectedHkn([]);
        setExcludeHkn([]);
        setPlzPrefix('');
        setPreview(null);
      } else {
        const errData = await response.json();
        setError(errData.message || 'Auswahl fehlgeschlagen');
      }
    } catch (err) {
      setError('Fehler bei der Auswahl');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Export CSV — Felder optional. Wenn `customFields` übergeben werden,
  // gehen sie an den Endpoint mit; sonst nutzt das Backend seine Defaults.
  const handleExport = async (runId: number, customFields?: string[]) => {
    try {
      const body = customFields && customFields.length > 0 ? JSON.stringify({ fields: customFields }) : undefined;
      const response = await authFetch(getIngestUrl(`/auswahl/runs/${runId}/export`), {
        method: 'POST',
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body,
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `auswahl_${runId}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        loadData();
        setSuccess('Export erfolgreich');
      } else {
        setError('Export fehlgeschlagen');
      }
    } catch (err) {
      setError('Fehler beim Export');
      console.error(err);
    }
  };

  const toggleExportField = (key: string) => {
    setSelectedExportFields(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const setAllExportFields = (selectAll: boolean) => {
    if (selectAll) setSelectedExportFields(new Set(exportFields.map(f => f.key)));
    else setSelectedExportFields(new Set());
  };

  const resetExportFieldsToDefault = () => {
    setSelectedExportFields(new Set(DEFAULT_EXPORT_FIELDS));
  };

  // Revert selection
  const handleRevert = async (runId: number) => {
    if (!window.confirm('Auswahl wirklich rückgängig machen? CHK wird auf Leerzeichen zurückgesetzt.')) {
      return;
    }

    try {
      const response = await authFetch(getIngestUrl(`/auswahl/runs/${runId}/revert`), {
        method: 'POST'
      });

      if (response.ok) {
        setSuccess('Auswahl rückgängig gemacht');
        loadData();
        setSelectedRun(null);
      } else {
        setError('Rückgängig machen fehlgeschlagen');
      }
    } catch (err) {
      setError('Fehler beim Rückgängig machen');
      console.error(err);
    }
  };

  // Delete run
  const handleDelete = async (runId: number) => {
    if (!window.confirm('Lauf wirklich löschen?')) {
      return;
    }

    try {
      const response = await authFetch(getIngestUrl(`/auswahl/runs/${runId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccess('Lauf gelöscht');
        loadData();
        setSelectedRun(null);
      } else {
        setError('Löschen fehlgeschlagen');
      }
    } catch (err) {
      setError('Fehler beim Löschen');
      console.error(err);
    }
  };

  // Load records for a run
  const loadRunRecords = async (runId: number) => {
    try {
      const response = await authFetch(getIngestUrl(`/auswahl/runs/${runId}/records?limit=100`));
      if (response.ok) {
        const data = await response.json();
        setSelectedRecords(data.data || []);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Records:', err);
    }
  };

  // View run details
  const handleViewRun = async (run: AuswahlRun) => {
    setSelectedRun(run);
    await loadRunRecords(run.id);
  };

  // Save profile
  const handleSaveProfile = async () => {
    const name = prompt('Profilname:');
    if (!name) return;

    const description = prompt('Beschreibung (optional):') || '';

    try {
      const response = await authFetch(getIngestUrl('/auswahl/profiles'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          description,
          filter: buildFilter()
        })
      });

      if (response.ok) {
        setSuccess('Profil gespeichert');
        loadData();
      } else {
        setError('Profil speichern fehlgeschlagen');
      }
    } catch (err) {
      setError('Fehler beim Speichern');
      console.error(err);
    }
  };

  // Load profile
  const handleLoadProfile = (profile: Profile) => {
    const filter = profile.filterConfig;
    setSelectedHkn(filter.hkn || []);
    setExcludeHkn(filter.hknExclude || []);
    setPlzPrefix((filter.plzPrefix || []).join(', '));
    setActiveTab('new');
    setSuccess(`Profil "${profile.name}" geladen`);
  };

  // Delete profile
  const handleDeleteProfile = async (profileId: number) => {
    if (!window.confirm('Profil wirklich löschen?')) return;

    try {
      const response = await authFetch(getIngestUrl(`/auswahl/profiles/${profileId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccess('Profil gelöscht');
        loadData();
      } else {
        setError('Löschen fehlgeschlagen');
      }
    } catch (err) {
      setError('Fehler beim Löschen');
      console.error(err);
    }
  };

  // Toggle HKN selection
  const toggleHkn = (hkn: string, list: 'include' | 'exclude') => {
    if (list === 'include') {
      if (selectedHkn.includes(hkn)) {
        setSelectedHkn(selectedHkn.filter(h => h !== hkn));
      } else {
        setSelectedHkn([...selectedHkn, hkn]);
        setExcludeHkn(excludeHkn.filter(h => h !== hkn));
      }
    } else {
      if (excludeHkn.includes(hkn)) {
        setExcludeHkn(excludeHkn.filter(h => h !== hkn));
      } else {
        setExcludeHkn([...excludeHkn, hkn]);
        setSelectedHkn(selectedHkn.filter(h => h !== hkn));
      }
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return formatDateTime(dateStr);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done': return '#10b981';
      case 'exported': return '#3b82f6';
      case 'running': return '#f59e0b';
      case 'reverted': return '#6b7280';
      case 'error': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'done': return 'Fertig';
      case 'exported': return 'Exportiert';
      case 'running': return 'Läuft';
      case 'reverted': return 'Rückgängig';
      case 'error': return 'Fehler';
      default: return status;
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <SubPageHeader title="Auswahl" icon={<ListChecks size={28} weight="fill" />} />

      {/* Messages */}
      {error && (
        <div style={{ padding: '1rem 2rem', backgroundColor: '#fee2e2', color: '#dc2626', borderBottom: '1px solid #fca5a5' }}>
          {error}
          <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        </div>
      )}
      {success && (
        <div style={{ padding: '1rem 2rem', backgroundColor: '#d1fae5', color: '#059669', borderBottom: '1px solid #6ee7b7' }}>
          {success}
          <button onClick={() => setSuccess(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', padding: '0 2rem' }}>
        <div style={{ display: 'flex', gap: '2rem' }}>
          {[
            { id: 'new', label: 'Neue Auswahl', icon: <Plus size={18} /> },
            { id: 'runs', label: 'Läufe', icon: <ListChecks size={18} /> },
            { id: 'profiles', label: 'Profile', icon: <FloppyDisk size={18} /> }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                padding: '1rem 0',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid #003366' : '2px solid transparent',
                color: activeTab === tab.id ? '#003366' : '#6b7280',
                fontWeight: activeTab === tab.id ? 600 : 400,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      </div>

      <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem' }}>
        {/* New Selection Tab */}
        {activeTab === 'new' && (
          <>
          {/* Aus Planung übernehmen — full width above the two-column grid */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '2px solid #003366' }}>
              <h3 style={{ margin: '0 0 0.25rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <ListChecks size={20} /> Aus Planung übernehmen
              </h3>
              <p style={{ margin: '0 0 1rem 0', color: '#6b7280', fontSize: '0.875rem' }}>
                Wählt HKN-Liste und Anzahl automatisch aus den Einsatz-Werten der Planung.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, fontSize: '0.875rem' }}>
                    Planung
                  </label>
                  <select
                    value={planungId}
                    onChange={(e) => setPlanungId(e.target.value ? parseInt(e.target.value, 10) : '')}
                    style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.95rem' }}
                  >
                    <option value="">— wählen —</option>
                    {planungenList.map(p => (
                      <option key={p.id} value={p.id}>{p.name}{p.status !== 'aktiv' ? ` (${p.status})` : ''}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, fontSize: '0.875rem' }}>
                    Kalenderwoche
                  </label>
                  <select
                    value={planungKwId}
                    onChange={(e) => setPlanungKwId(e.target.value ? parseInt(e.target.value, 10) : '')}
                    disabled={!planungId || planungKws.length === 0}
                    style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.95rem' }}
                  >
                    <option value="">— wählen —</option>
                    {planungKws.map(k => (
                      <option key={k.id} value={k.id}>{k.kw_label}/{k.jahr}</option>
                    ))}
                  </select>
                </div>
              </div>

              {planungLoading && (
                <div style={{ marginTop: '1rem', color: '#6b7280', fontSize: '0.875rem' }}>Lade Planungs-Daten...</div>
              )}

              {!planungLoading && planungZeilen.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <div style={{ marginBottom: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                    <strong>{aktiveZeilen.length}</strong>/{planungZeilen.length} Quellen aktiv · Summe Einsatz: <strong>{aktiveZeilen.reduce((a, z) => a + z.einsatz, 0).toLocaleString()}</strong>
                    {aktiveZeilen.length !== planungZeilen.length && (
                      <span style={{ marginLeft: '0.5rem', color: '#9ca3af' }}>
                        (von Gesamt {planungZeilen.reduce((a, z) => a + z.einsatz, 0).toLocaleString()})
                      </span>
                    )}
                  </div>
                  <div style={{ maxHeight: '240px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '4px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                      <thead style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0 }}>
                        <tr>
                          <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb', width: '32px' }}>
                            <input
                              type="checkbox"
                              aria-label="Alle Quellen an/aus"
                              checked={aktiveZeilen.length === planungZeilen.length && planungZeilen.length > 0}
                              ref={el => { if (el) el.indeterminate = aktiveZeilen.length > 0 && aktiveZeilen.length < planungZeilen.length; }}
                              onChange={e => toggleAllQuellen(e.target.checked)}
                            />
                          </th>
                          <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Quelle</th>
                          <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>WKZ</th>
                          <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>HKN</th>
                          <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Kategorie</th>
                          <th style={{ padding: '0.5rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Einsatz</th>
                        </tr>
                      </thead>
                      <tbody>
                        {planungZeilen.map(z => {
                          const aktiv = !excludedQuelleIds.has(z.quelle.id);
                          const rowStyle: React.CSSProperties = {
                            opacity: aktiv ? 1 : 0.45,
                            backgroundColor: aktiv ? 'transparent' : '#f9fafb',
                          };
                          return (
                            <tr key={z.quelle.id} style={rowStyle}>
                              <td style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>
                                <input
                                  type="checkbox"
                                  checked={aktiv}
                                  onChange={() => toggleQuelle(z.quelle.id)}
                                />
                              </td>
                              <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{z.quelle.name}</td>
                              <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb', fontFamily: 'monospace' }}>{z.quelle.wkz || '-'}</td>
                              <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb', fontFamily: 'monospace' }}>{z.quelle.wpool_hkn || z.quelle.wkz || '-'}</td>
                              <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{z.quelle.kategorie}</td>
                              <td style={{ padding: '0.5rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>{z.einsatz.toLocaleString()}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {!planungLoading && planungId && planungKwId && planungZeilen.length === 0 && (
                <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#fef3c7', borderRadius: '4px', fontSize: '0.875rem', color: '#92400e' }}>
                  In dieser KW sind keine Einsatz-Werte hinterlegt.
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
            {/* Left: Filter */}
            <div>
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
                <h3 style={{ margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <FunnelSimple size={20} /> Filter
                </h3>

                {/* Name */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Name der Auswahl *
                  </label>
                  <input
                    type="text"
                    value={auswahlName}
                    onChange={(e) => setAuswahlName(e.target.value)}
                    placeholder="z.B. Mailing Januar 2025"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                {/* HKN/PLZ-Filter werden automatisch aus der gewählten Planung+KW abgeleitet */}
                {selectedHkn.length > 0 && (
                  <div style={{ marginBottom: '1.5rem', padding: '0.75rem', backgroundColor: '#eff6ff', borderRadius: '4px', fontSize: '0.875rem', color: '#1e40af' }}>
                    {selectedHkn.length} HKN aus Planung übernommen.
                  </div>
                )}

                {/* Selection Mode */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Auswahlmodus
                  </label>
                  <select
                    value={selectionMode}
                    onChange={(e) => setSelectionMode(e.target.value as any)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="random">Zufällig</option>
                    <option value="newest">Neueste zuerst</option>
                    <option value="oldest">Älteste zuerst</option>
                    <option value="all">Alle</option>
                  </select>
                </div>

                {/* Count */}
                {selectionMode !== 'all' && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                      Anzahl
                    </label>
                    <input
                      type="number"
                      value={requestedCount}
                      onChange={(e) => setRequestedCount(e.target.value)}
                      min="1"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                )}

                {/* Buttons */}
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button
                    onClick={handlePreview}
                    disabled={previewLoading || selectedHkn.length === 0}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: selectedHkn.length === 0 ? '#9ca3af' : '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: selectedHkn.length === 0 ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <FunnelSimple size={18} /> {previewLoading ? 'Lädt...' : 'Vorschau'}
                  </button>
                </div>
              </div>
            </div>

            {/* Right: Preview & Execute */}
            <div>
              {/* Preview Results */}
              {preview && (
                <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', marginBottom: '1.5rem' }}>
                  <h3 style={{ margin: '0 0 1rem 0' }}>Vorschau</h3>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#003366', marginBottom: '1rem' }}>
                    {preview.matchedCount.toLocaleString()} Adressen
                  </div>

                  {preview.byHkn.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#6b7280' }}>Nach HKN</h4>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {preview.byHkn.slice(0, 10).map(h => (
                          <span key={h.hkn} style={{ padding: '0.25rem 0.5rem', backgroundColor: '#f3f4f6', borderRadius: '4px', fontSize: '0.875rem' }}>
                            {h.hkn || '(leer)'}: {h.count.toLocaleString()}
                          </span>
                        ))}
                        {preview.byHkn.length > 10 && <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>+{preview.byHkn.length - 10} weitere</span>}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Execute */}
              <div style={{ backgroundColor: '#003366', borderRadius: '8px', padding: '1.5rem', color: 'white' }}>
                <h3 style={{ margin: '0 0 1rem 0' }}>Auswahl starten</h3>
                <p style={{ margin: '0 0 1.5rem 0', opacity: 0.9 }}>
                  {preview
                    ? `${selectionMode === 'all' ? preview.matchedCount.toLocaleString() : Math.min(parseInt(requestedCount, 10) || 0, preview.matchedCount).toLocaleString()} Adressen werden mit CHK='R' markiert.`
                    : 'Bitte zuerst Vorschau erstellen.'}
                </p>
                <button
                  onClick={handleExecute}
                  disabled={loading || !preview || !auswahlName.trim()}
                  style={{
                    padding: '1rem 2rem',
                    backgroundColor: !preview || loading ? '#6b7280' : '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: !preview || loading ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    width: '100%',
                    justifyContent: 'center'
                  }}
                >
                  <Play size={20} weight="fill" /> {loading ? 'Wird ausgeführt...' : 'Auswahl starten'}
                </button>
              </div>
            </div>
          </div>
          </>
        )}

        {/* Runs Tab */}
        {activeTab === 'runs' && (
          <div style={{ display: 'grid', gridTemplateColumns: selectedRun ? '1fr 1fr' : '1fr', gap: '2rem' }}>
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
              <h3 style={{ margin: '0 0 1rem 0' }}>Auswahl-Läufe</h3>
              {runs.length === 0 ? (
                <p style={{ color: '#6b7280' }}>Keine Läufe vorhanden</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Name</th>
                      <th style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Ausgewählt</th>
                      <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Datum</th>
                      <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map(run => (
                      <tr
                        key={run.id}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: selectedRun?.id === run.id ? '#dbeafe' : 'white'
                        }}
                        onClick={() => handleViewRun(run)}
                      >
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>{run.name}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>{run.selectedCount?.toLocaleString() || 0}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: getStatusColor(run.status),
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: '0.75rem'
                          }}>
                            {getStatusLabel(run.status)}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{formatDate(run.createdAt)}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                            {run.status === 'done' && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleExport(run.id); }}
                                style={{ padding: '0.25rem 0.5rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                title="CSV exportieren"
                              >
                                <Download size={16} />
                              </button>
                            )}
                            {run.status === 'done' && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRevert(run.id); }}
                                style={{ padding: '0.25rem 0.5rem', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                title="Rückgängig"
                              >
                                <ArrowCounterClockwise size={16} />
                              </button>
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDelete(run.id); }}
                              style={{ padding: '0.25rem 0.5rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                              title="Löschen"
                            >
                              <Trash size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Run Detail */}
            {selectedRun && (
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
                <h3 style={{ margin: '0 0 1rem 0' }}>{selectedRun.name}</h3>
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', fontSize: '0.875rem' }}>
                    <div><strong>Modus:</strong> {selectedRun.selectionMode}</div>
                    <div><strong>Angefordert:</strong> {selectedRun.requestedCount?.toLocaleString() || 'Alle'}</div>
                    <div><strong>Gefunden:</strong> {selectedRun.matchedCount?.toLocaleString()}</div>
                    <div><strong>Ausgewählt:</strong> {selectedRun.selectedCount?.toLocaleString()}</div>
                    <div><strong>Status:</strong> {getStatusLabel(selectedRun.status)}</div>
                    <div><strong>Erstellt:</strong> {formatDate(selectedRun.createdAt)}</div>
                  </div>
                </div>

                {/* Felder-Auswahl für Export */}
                <div style={{ margin: '1rem 0', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <h4 style={{ margin: 0 }}>Export-Felder ({selectedExportFields.size}/{exportFields.length})</h4>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => setAllExportFields(true)}
                        style={{ padding: '0.25rem 0.6rem', fontSize: '0.8rem', background: 'none', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        Alle
                      </button>
                      <button
                        onClick={() => setAllExportFields(false)}
                        style={{ padding: '0.25rem 0.6rem', fontSize: '0.8rem', background: 'none', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        Keine
                      </button>
                      <button
                        onClick={resetExportFieldsToDefault}
                        style={{ padding: '0.25rem 0.6rem', fontSize: '0.8rem', background: 'none', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        Standard
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.4rem 0.75rem', fontSize: '0.85rem' }}>
                    {exportFields.map(f => (
                      <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={selectedExportFields.has(f.key)}
                          onChange={() => toggleExportField(f.key)}
                        />
                        <span>{f.label} <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>({f.key})</span></span>
                      </label>
                    ))}
                  </div>
                  <button
                    onClick={() => handleExport(selectedRun.id, Array.from(selectedExportFields))}
                    disabled={selectedExportFields.size === 0}
                    style={{
                      marginTop: '0.75rem',
                      padding: '0.5rem 1rem',
                      backgroundColor: selectedExportFields.size === 0 ? '#9ca3af' : '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: selectedExportFields.size === 0 ? 'not-allowed' : 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      fontWeight: 500,
                    }}
                  >
                    <Download size={16} /> Mit ausgewählten Feldern exportieren
                  </button>
                </div>

                <h4 style={{ margin: '1rem 0 0.5rem 0' }}>Beispiel-Datensätze (max. 100)</h4>
                <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '4px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                    <thead style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0 }}>
                      <tr>
                        <th style={{ padding: '0.5rem', textAlign: 'left' }}>VNM</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left' }}>NNM</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left' }}>PLZ</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left' }}>ORT</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left' }}>HKN</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRecords.map((rec, i) => (
                        <tr key={i}>
                          <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{rec.vnm}</td>
                          <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{rec.nnm}</td>
                          <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{rec.plz}</td>
                          <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{rec.ort}</td>
                          <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{rec.hkn}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Profiles Tab */}
        {activeTab === 'profiles' && (
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
            <h3 style={{ margin: '0 0 1rem 0' }}>Gespeicherte Profile</h3>
            {profiles.length === 0 ? (
              <p style={{ color: '#6b7280' }}>Keine Profile vorhanden. Erstellen Sie eine Auswahl und speichern Sie den Filter als Profil.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Name</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Beschreibung</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Filter</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {profiles.map(profile => (
                    <tr key={profile.id}>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb', fontWeight: 500 }}>{profile.name}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>{profile.description || '-'}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb', fontSize: '0.875rem' }}>
                        {profile.filterConfig?.hkn?.length > 0 && <span>HKN: {profile.filterConfig.hkn.join(', ')}</span>}
                        {profile.filterConfig?.plzPrefix?.length > 0 && <span style={{ marginLeft: '0.5rem' }}>PLZ: {profile.filterConfig.plzPrefix.join(', ')}</span>}
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
                        <button
                          onClick={() => handleLoadProfile(profile)}
                          style={{ padding: '0.5rem 1rem', backgroundColor: '#003366', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '0.5rem' }}
                        >
                          Laden
                        </button>
                        <button
                          onClick={() => handleDeleteProfile(profile.id)}
                          style={{ padding: '0.5rem 1rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          Löschen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
